import React from "react";

import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  Button,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import BoltIcon from "@mui/icons-material/Bolt";

import { formatEpisodeLengthToHumanFriendlyDuration } from "../../../utils/formatEpisodeLengthToHumanFriendlyDuration";

interface SummaryProps {
  files: File[];
  fileDurationInSeconds: number;
  name: string;
  selectedProject: { label: string; id: string } | null;
  handleOpenPricingPlanDialog: () => void;
  isPlanLimitReached: boolean;
}

export default function Summary({
  files,
  fileDurationInSeconds,
  name,
  selectedProject,
  handleOpenPricingPlanDialog,
  isPlanLimitReached,
}: SummaryProps) {
  const file = files[0];

  return (
    <Box sx={{ width: "100%" }}>
      {isPlanLimitReached && (
        <Box sx={{ py: 3 }}>
          <Alert
            severity="error"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: 3,
              "& .MuiAlert-message": {
                fontSize: 14,
                fontWeight: 500,
              },
            }}
            action={
              <Button
                onClick={handleOpenPricingPlanDialog}
                variant="contained"
                sx={{
                  borderRadius: 5,
                  textTransform: "none",
                }}
                startIcon={<BoltIcon sx={{ color: "#ffd54f", fontSize: 18 }} />}
              >
                Upgrade
              </Button>
            }
          >
            You have reached the limit of maximum minutes allowed for your plan.
            Please upgrade your plan to continue.
          </Alert>
        </Box>
      )}
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          textAlign: "center",
          color: "#23272E",
          fontWeight: 700,
        }}
      >
        Review & Upload
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: 4,
          textAlign: "center",
          color: "#666",
        }}
      >
        Please review your episode details before uploading.
      </Typography>

      {/* File Details */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, color: "#23272E", fontWeight: 600 }}
        >
          File Information
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: file?.type.startsWith("audio")
                ? "#e3f2fd"
                : "#ffebee",
            }}
          >
            {file?.type.startsWith("audio") ? (
              <AudioFileIcon sx={{ color: "#1976d2", fontSize: 24 }} />
            ) : (
              <VideoFileIcon sx={{ color: "#d32f2f", fontSize: 24 }} />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#23272E" }}
            >
              {file?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {file?.type.split("/")[1].toUpperCase()} •{" "}
              {formatEpisodeLengthToHumanFriendlyDuration(
                fileDurationInSeconds,
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={file?.type.split("/")[1].toUpperCase()}
            size="small"
            sx={{
              backgroundColor: "#e8f5e8",
              color: "#2e7d32",
              fontWeight: 600,
            }}
          />
          <Chip
            label={`${((file?.size || 0) / (1024 * 1024)).toFixed(1)} MB`}
            size="small"
            sx={{
              backgroundColor: "#f3e5f5",
              color: "#7b1fa2",
              fontWeight: 600,
            }}
          />
          <Chip
            icon={<CheckCircleIcon />}
            label="Ready to upload"
            size="small"
            sx={{
              backgroundColor: "#e8f5e8",
              color: "#2e7d32",
              fontWeight: 600,
            }}
          />
        </Box>
      </Paper>

      {/* Episode Details */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, color: "#23272E", fontWeight: 600 }}
        >
          Episode Details
        </Typography>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
              Episode Name
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#23272E" }}
            >
              {name}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
              Project
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#23272E" }}
            >
              {selectedProject?.label}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
              Duration
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#23272E" }}
            >
              {formatEpisodeLengthToHumanFriendlyDuration(
                fileDurationInSeconds,
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
